import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Collapse, IconButton, Paper, Typography } from '@mui/material';
import { DeleteOutline, Close } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { getAllTickets, deleteTicket } from '../../../services/feedbackService';
import { FeedbackTicketTable, useFeedbackViewMode, ViewModeToggle } from '../../feedback/feedbackShared';
import { FeedbackTicketCard } from '../../feedback/FeedbackTicketCard';
import { FeedbackFilters } from '../../feedback/FeedbackFilters';
import { useFeedbackFilters } from '../../feedback/useFeedbackFilters';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../../components/shared/ConfirmDialog';
import { Pagination } from '../../../components/tables/Pagination';
import type { FeedbackTicket } from '../../../types/feedback';

/** "Show everything" sentinel for the page-size selector (MUI convention). */
const ALL_PAGE_SIZE = -1;
const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_STORAGE_KEY = 'admin-feedback-page-size';
/** 50 default, with the option to go higher, and "All" for the full list. */
const FEEDBACK_PAGE_SIZE_OPTIONS: (number | { label: string; value: number })[] = [
  50, 100, 200, { label: 'All', value: ALL_PAGE_SIZE },
];

function readStoredPageSize(): number {
  try {
    const v = Number(localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
    if (v === ALL_PAGE_SIZE || [50, 100, 200].includes(v)) return v;
  } catch { /* ignore */ }
  return DEFAULT_PAGE_SIZE;
}

export const AdminFeedbackPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<FeedbackTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useFeedbackViewMode('admin-feedback-view');
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [confirmDlg, setConfirmDlg] = useState<ConfirmDialogState>(CONFIRM_INITIAL);

  const { state: filterState, update: updateFilters, clear: clearFilters,
    options: filterOptions, filtered, activeCount } = useFeedbackFilters(tickets);

  // Client-side pagination over the filtered list. Default 50/page so the page
  // renders fast even with a large backlog; "All" is available for triage sweeps.
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(readStoredPageSize);

  // Stable signature of the active filters — string so the reset effect fires
  // only when the filters genuinely change, not on every render.
  const filterSig = useMemo(() => JSON.stringify({
    q: filterState.search,
    s: [...filterState.statuses].sort(),
    t: [...filterState.types].sort(),
    age: filterState.ageDays,
    v: [...filterState.versions].sort(),
    f: [...filterState.features].sort(),
    sev: [...filterState.severities].sort(),
    pri: [...filterState.priorities].sort(),
    by: [...filterState.submittedBy].sort(),
  }), [filterState]);

  // Changing the filters jumps back to the first page.
  useEffect(() => { setPage(0); }, [filterSig]);

  // Keep the page in range if the result set shrinks (e.g. after a bulk delete).
  useEffect(() => {
    if (pageSize === ALL_PAGE_SIZE) return;
    const maxPage = Math.max(0, Math.ceil(filtered.length / pageSize) - 1);
    setPage((p) => Math.min(p, maxPage));
  }, [filtered.length, pageSize]);

  const paged = useMemo(() => (
    pageSize === ALL_PAGE_SIZE ? filtered : filtered.slice(page * pageSize, page * pageSize + pageSize)
  ), [filtered, page, pageSize]);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setPage(0);
    try { localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(size)); } catch { /* ignore */ }
  }, []);

  const fetchTickets = useCallback(async () => {
    try { setTickets(await getAllTickets()); }
    catch { /* */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const openTicket = (t: FeedbackTicket) => navigate(`/admin/feedback/${t.FeedbackTicketKey}`);

  const toggleSelect = useCallback((key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedKeys((prev) => {
      // Select-all toggles only the tickets on the current page. Selections on
      // other pages are preserved, so the choice survives paging + filter edits.
      const visibleKeys = paged.map((t) => t.FeedbackTicketKey);
      const allVisibleSelected = visibleKeys.length > 0 && visibleKeys.every((k) => prev.has(k));
      const next = new Set(prev);
      if (allVisibleSelected) visibleKeys.forEach((k) => next.delete(k));
      else visibleKeys.forEach((k) => next.add(k));
      return next;
    });
  }, [paged]);

  const clearSelection = useCallback(() => setSelectedKeys(new Set()), []);

  const handleBulkDelete = useCallback(async () => {
    setDeleting(true);
    try {
      const keys = Array.from(selectedKeys);
      await Promise.all(keys.map((k) => deleteTicket(k).catch(() => null)));
      setSelectedKeys(new Set());
      await fetchTickets();
    } finally {
      setDeleting(false);
    }
  }, [selectedKeys, fetchTickets]);

  const selectedCount = selectedKeys.size;
  const hasSelection = useMemo(() => selectedCount > 0, [selectedCount]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}><CircularProgress /></Box>;

  const pager = filtered.length > 0 ? (
    <Pagination
      page={page}
      pageSize={pageSize}
      total={filtered.length}
      onPageChange={setPage}
      onPageSizeChange={handlePageSizeChange}
      pageSizeOptions={FEEDBACK_PAGE_SIZE_OPTIONS}
      entityLabel="tickets"
    />
  ) : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2, gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1rem' }}>Feedback Triage</Typography>
        </Box>
        {tickets.length > 0 && <ViewModeToggle value={view} onChange={setView} />}
      </Box>

      {tickets.length > 0 && (
        <FeedbackFilters
          state={filterState}
          update={updateFilters}
          clear={clearFilters}
          options={filterOptions}
          activeCount={activeCount}
          total={tickets.length}
          shown={filtered.length}
          isAdmin
        />
      )}

      {/* Bulk-action bar — Collapse gives a smooth slide-down/up instead of a
          layout jump when selection starts/ends. Always mounted so animation works. */}
      <Collapse in={hasSelection} timeout={240} mountOnEnter unmountOnExit>
        <Paper elevation={0} sx={{
          mb: 2, px: 2, py: 1,
          borderRadius: 1.5, border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {selectedCount} selected
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="contained" color="error" disabled={deleting}
            startIcon={<DeleteOutline />}
            onClick={() => setConfirmDlg({
              open: true,
              title: 'Delete Selected Tickets',
              message: `Permanently delete ${selectedCount} ticket${selectedCount !== 1 ? 's' : ''}? This cannot be undone.`,
              confirmLabel: 'Delete', confirmColor: 'error',
              onConfirm: handleBulkDelete,
            })}
            sx={{ textTransform: 'none' }}>
            {deleting ? 'Deleting...' : `Delete ${selectedCount}`}
          </Button>
          <IconButton size="small" onClick={clearSelection} title="Clear selection">
            <Close fontSize="small" />
          </IconButton>
        </Paper>
      </Collapse>

      {pager}

      {tickets.length === 0 ? (
        <Typography color="text.secondary">No feedback tickets yet.</Typography>
      ) : filtered.length === 0 ? (
        <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
          No tickets match the current filters.
        </Typography>
      ) : view === 'cards' ? (
        <Box sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        }}>
          {paged.map((t) => (
            <FeedbackTicketCard key={t.FeedbackTicketKey} ticket={t} showSubmitter
              selectable
              selected={selectedKeys.has(t.FeedbackTicketKey)}
              onToggleSelect={() => toggleSelect(t.FeedbackTicketKey)}
              onClick={() => openTicket(t)} />
          ))}
        </Box>
      ) : (
        <FeedbackTicketTable
          tickets={paged}
          onRowClick={openTicket}
          showSubmitter
          showAdminPriority
          selectable
          selectedKeys={selectedKeys}
          onToggleSelect={toggleSelect}
          onToggleSelectAll={toggleSelectAll}
        />
      )}

      {filtered.length > pageSize && pageSize !== ALL_PAGE_SIZE && (
        <Box sx={{ mt: 1 }}>{pager}</Box>
      )}

      <ConfirmDialog state={confirmDlg} onClose={() => setConfirmDlg(CONFIRM_INITIAL)} />
    </Box>
  );
};
